import { useState } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { ImplementationLine, Product } from '../types';

interface ImplementationSectionProps {
  included: boolean;
  lines: ImplementationLine[];
  products: Product[];
  onToggleIncluded: (included: boolean) => void;
  onUpdateLine: (lineId: string, updates: Partial<ImplementationLine>) => void;
  onAddLine: () => void;
  onRemoveLine: (lineId: string) => void;
  subtotal: number;
  onDemandRate: number;
  projectMgmtRate: number;
  onUpdateOnDemandRate: (rate: number) => void;
  onUpdateProjectMgmtRate: (rate: number) => void;
}

const ImplementationSection = ({
  included,
  lines,
  products,
  onToggleIncluded,
  onUpdateLine,
  onAddLine,
  onRemoveLine,
  subtotal,
  onDemandRate,
  projectMgmtRate,
  onUpdateOnDemandRate,
  onUpdateProjectMgmtRate,
}: ImplementationSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const implementationProducts = products.filter((p) => 
    !p.productName?.toLowerCase().includes('license') && 
    !p.groupName?.toLowerCase().includes('license')
  );

  const getProductOptions = () => {
    // Sort by group name first, then by product name
    const sortedProducts = [...implementationProducts].sort((a, b) => {
      const groupComparison = (a.groupName || '').localeCompare(b.groupName || '');
      if (groupComparison !== 0) return groupComparison;
      return (a.productName || '').localeCompare(b.productName || '');
    });

    return sortedProducts.map((p) => ({
      value: p.id,
      label: `${p.groupName} - ${p.productName}`,
      description: `€${p.unitPrice.toFixed(2)}`,
    }));
  };

  const handleProductChange = (lineId: string, productId: string) => {
    const product = implementationProducts.find((p) => p.id === productId);
    if (product) {
      onUpdateLine(lineId, {
        productId: product.id,
        productName: product.productName,
        groupName: product.groupName,
        hourlyRate: product.unitPrice,
      });
    }
  };

  const calculateLineTotal = (line: ImplementationLine) => {
    // Safe calculation with proper null/undefined handling
    const hourlyRate = Number(line.hourlyRate) || Number(line.unitPrice) || 0;
    const hours = Number(line.hours) || Number(line.quantity) || 0;
    return hourlyRate * hours;
  };

  // Calculate total hours from all implementation service lines with safe handling
  const totalHours = lines.reduce((sum, line) => {
    const hours = Number(line.hours) || Number(line.quantity) || 0;
    return sum + hours;
  }, 0);

  // Safe rate handling
  const safeOnDemandRate = Number(onDemandRate) || 0;
  const safeProjectMgmtRate = Number(projectMgmtRate) || 0;

  // On Demand Services: 15% of total hours
  const onDemandHours = totalHours * 0.15;
  const onDemandTotal = onDemandHours * safeOnDemandRate;

  // Project Management: 15% of (total hours + on demand hours)
  const projectMgmtHours = (totalHours + onDemandHours) * 0.15;
  const projectMgmtTotal = projectMgmtHours * safeProjectMgmtRate;

  // Total Implementation Services
  const safeSubtotal = Number(subtotal) || 0;
  const totalImplementationServices = safeSubtotal + onDemandTotal + projectMgmtTotal;

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 hover:bg-muted rounded transition-colors duration-150"
            aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
          >
            <Icon
              name={isExpanded ? 'ChevronDown' : 'ChevronRight'}
              size={20}
              className="text-text-secondary"
            />
          </button>
          <Icon name="Wrench" size={20} className="text-primary" />
          <h2 className="text-lg font-semibold text-text-primary">Implementation Services</h2>
        </div>
        <Checkbox
          label="Include in Quote"
          checked={included}
          onChange={(e) => onToggleIncluded(e.target.checked)}
        />
      </div>

      {isExpanded && included && (
        <div className="p-6 space-y-4">
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              iconName="Plus"
              iconPosition="left"
              onClick={onAddLine}
            >
              Add Service
            </Button>
          </div>

          <div className="overflow-visible">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-muted">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary border-b border-border w-1/5">
                    Group
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary border-b border-border w-1/4">
                    Service
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-text-primary border-b border-border">
                    Hourly Rate (€)
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-text-primary border-b border-border">
                    Hours
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-text-primary border-b border-border">
                    Total (€)
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-text-primary border-b border-border">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-text-secondary">
                      <Icon name="Wrench" size={48} className="mx-auto mb-2 opacity-30" />
                      <p>No implementation services added yet. Click "Add Service" to get started.</p>
                    </td>
                  </tr>
                ) : (
                  lines.map((line) => {
                    const total = calculateLineTotal(line);
                    const displayHourlyRate = Number(line.hourlyRate) || Number(line.unitPrice) || 0;
                    const displayHours = Number(line.hours) || Number(line.quantity) || 0;
                    
                    return (
                      <tr key={line.id} className="border-b border-border hover:bg-muted/50">
                        <td className="px-4 py-3">
                          <Input
                            type="text"
                            value={line.group || line.groupName || ''}
                            onChange={(e) => onUpdateLine(line.id, { group: e.target.value, groupName: e.target.value })}
                            placeholder="Group name"
                            className="text-sm"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Select
                            options={getProductOptions()}
                            value={line.productId}
                            onChange={(value) => handleProductChange(line.id, value as string)}
                            placeholder="Select service"
                            className="min-w-[200px]"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            type="number"
                            value={displayHourlyRate}
                            onChange={(e) => {
                              const rate = parseFloat(e.target.value) || 0;
                              onUpdateLine(line.id, { hourlyRate: rate, unitPrice: rate });
                            }}
                            className="text-right font-mono"
                            min="0"
                            step="0.01"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            type="number"
                            value={displayHours}
                            onChange={(e) => {
                              const hours = parseFloat(e.target.value) || 0;
                              onUpdateLine(line.id, { hours: hours, quantity: hours });
                            }}
                            className="text-right"
                            min="0"
                            step="0.5"
                          />
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm text-text-primary">
                          €{total.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            iconName="Trash2"
                            onClick={() => onRemoveLine(line.id)}
                            className="text-error hover:text-error hover:bg-error/10"
                            aria-label="Remove line"
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {lines.length > 0 && (
                <tfoot>
                  <tr className="bg-muted font-semibold">
                    <td colSpan={4} className="px-4 py-3 text-right text-sm text-text-primary">
                      Subtotal:
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm text-primary">
                      €{safeSubtotal.toFixed(2)}
                    </td>
                    <td></td>
                  </tr>
                  
                  {/* On Demand Services Row */}
                  <tr className="border-t border-border">
                    <td colSpan={2} className="px-4 py-3 text-left text-sm text-text-primary">
                      On demand services (estimate for meetings, quality assurance, ...)
                    </td>
                    <td className="px-4 py-3">
                      <Input
                        type="number"
                        value={safeOnDemandRate}
                        onChange={(e) => onUpdateOnDemandRate(parseFloat(e.target.value) || 0)}
                        className="text-right font-mono"
                        min="0"
                        step="0.01"
                      />
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm text-text-secondary">
                      {onDemandHours.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm text-text-primary">
                      €{onDemandTotal.toFixed(2)}
                    </td>
                    <td></td>
                  </tr>

                  {/* Project Management Row */}
                  <tr className="border-t border-border">
                    <td colSpan={2} className="px-4 py-3 text-left text-sm text-text-primary">
                      Project Overhead 15%
                    </td>
                    <td className="px-4 py-3">
                      <Input
                        type="number"
                        value={safeProjectMgmtRate}
                        onChange={(e) => onUpdateProjectMgmtRate(parseFloat(e.target.value) || 0)}
                        className="text-right font-mono"
                        min="0"
                        step="0.01"
                      />
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm text-text-secondary">
                      {projectMgmtHours.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm text-text-primary">
                      €{projectMgmtTotal.toFixed(2)}
                    </td>
                    <td></td>
                  </tr>

                  {/* Empty spacing row */}
                  <tr>
                    <td colSpan={6} className="py-2"></td>
                  </tr>

                  {/* Total Implementation Services Row */}
                  <tr className="bg-primary/10 border-t-2 border-primary">
                    <td colSpan={4} className="px-4 py-3 text-right text-base font-bold text-text-primary">
                      TOTAL IMPLEMENTATION SERVICES:
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-base font-bold text-primary">
                      €{totalImplementationServices.toFixed(2)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {isExpanded && !included && (
        <div className="p-6 text-center text-text-secondary">
          <Icon name="WrenchIcon" size={48} className="mx-auto mb-2 opacity-30" />
          <p>Implementation services excluded from quote</p>
        </div>
      )}
    </div>
  );
};

export default ImplementationSection;